/**
 * @file Reveal-оверлеи онлайн-белота: аукционный контракт и combo после 1-й взятки.
 *
 * Аукцион — 1:1 с локальным эффектом из
 * {@link client/src/components/BelotArmenianLocalLobby.jsx}; combo — затемнение,
 * веер подписей комбо и вылет «+N CP» (GSAP).
 *
 * @module components/online/BelotOnlineRevealOverlay
 */

import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { cn } from '../../poker-ui/utils/cn';
import './belot/belotOnlineRevealOverlay.css';
import '../belot/ArmenianAuctionRevealFx.css';

const TRUMP_NO_TRUMP = 'no_trump';
const TRUMP_ALL_TRUMP = 'all_trump';

function trumpDisplay(trump) {
  if (trump === TRUMP_ALL_TRUMP || trump === 'all_trump') {
    return { line1: 'AT', tone: '' };
  }
  if (trump === TRUMP_NO_TRUMP || trump === 'no_trump') {
    return { line1: 'БК', tone: 'belot-auction-reveal-orb-notrump' };
  }
  const n = Number(trump);
  if (Number.isInteger(n) && n >= 0 && n <= 3) {
    const symbol = ['♣', '♦', '♥', '♠'][n];
    const toneByIdx = [
      'belot-auction-reveal-orb-club',
      'belot-auction-reveal-orb-diamond',
      'belot-auction-reveal-orb-heart',
      'belot-auction-reveal-orb-spade',
    ];
    return { line1: symbol, tone: toneByIdx[n] };
  }
  return { line1: '—', tone: '' };
}

/**
 * @param {object} props
 * @param {{ token: string|number, points: number, trump: unknown, multiplier: number, fromX: number, fromY: number }|null} [props.fx]
 * @param {{
 *   token: number,
 *   labels: string[],
 *   cpFlyText: string,
 *   prefersReducedMotion?: boolean,
 * }|null} [props.comboFx]
 * @param {boolean} [props.embedded] — `absolute inset-0` внутри стола вместо `fixed`
 * @param {number} [props.zIndex]
 */
export function BelotOnlineRevealOverlay({ fx = null, comboFx = null, embedded = false, zIndex = 235 }) {
  const comboRootRef = useRef(null);
  const comboFanRef = useRef(null);
  const comboCpRef = useRef(null);

  useEffect(() => {
    if (!comboFx || comboFx.prefersReducedMotion) return undefined;
    const root = comboRootRef.current;
    const fan = comboFanRef.current;
    const cp = comboCpRef.current;
    if (!root || !fan || !cp) return undefined;
    let ctx = null;
    ctx = gsap.context(() => {
      gsap.fromTo(fan, { scale: 0.85, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.35, ease: 'power2.out' });
      gsap.fromTo(
        cp,
        { scale: 0.5, opacity: 0, y: 12 },
        { scale: 1, opacity: 1, y: 0, duration: 0.28, delay: 0.12, ease: 'back.out(1.6)' },
      );
      gsap.to(cp, {
        x: '42vw',
        y: '-38vh',
        scale: 0.72,
        opacity: 0,
        delay: 0.95,
        duration: 0.85,
        ease: 'power2.inOut',
      });
    }, root);
    return () => {
      if (ctx) ctx.revert();
    };
  }, [comboFx]);

  if (comboFx && Array.isArray(comboFx.labels)) {
    const wrapCls = embedded ? 'absolute inset-0 flex' : 'fixed inset-0 z-[235] flex';
    const labels = comboFx.labels.length ? comboFx.labels : ['—'];
    return (
      <div
        ref={comboRootRef}
        className={cn(wrapCls, 'pointer-events-none items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]')}
        style={{ zIndex: embedded ? zIndex : undefined }}
        aria-hidden
      >
        <div ref={comboFanRef} className="flex max-w-[min(96vw,28rem)] flex-col items-center gap-3">
          <p className="text-[10px] font-black uppercase tracking-[0.16em] text-amber-200/95">Комбо</p>
          <div className="flex flex-wrap items-center justify-center gap-1.5">
            {labels.slice(0, 10).map((lab) => (
              <span
                key={`${comboFx.token}-${lab}`}
                className="rounded-md border border-amber-300/45 bg-amber-950/40 px-2 py-1 text-[11px] font-semibold text-amber-50/95 shadow-md"
              >
                {lab}
              </span>
            ))}
          </div>
          <div
            ref={comboCpRef}
            className="mt-1 select-none text-2xl font-black tabular-nums text-amber-100 drop-shadow-[0_0_12px_rgba(251,191,36,0.35)]"
          >
            {comboFx.cpFlyText}
          </div>
        </div>
      </div>
    );
  }

  if (!fx) return null;
  const trumpView = trumpDisplay(fx.trump);
  const fromX = Number(fx.fromX || 0);
  const fromY = Number(fx.fromY || 0);
  const revealDistance = Math.hypot(fromX, fromY);
  const revealAngleDeg = (Math.atan2(-fromY, -fromX) * 180) / Math.PI;
  const multiplier = Number(fx.multiplier || 1);
  const wrapCls = embedded ? 'absolute inset-0 z-[235] flex' : 'belot-auction-reveal-backdrop fixed inset-0 z-[235] flex';
  return (
    <div className={cn(wrapCls, 'items-center justify-center p-4')} style={embedded ? { zIndex } : undefined}>
      <div
        className="belot-auction-reveal-shell"
        style={{
          '--belot-reveal-from-x': `${fromX.toFixed(2)}px`,
          '--belot-reveal-from-y': `${fromY.toFixed(2)}px`,
          '--belot-reveal-distance': `${revealDistance.toFixed(2)}`,
          '--belot-reveal-angle': `${revealAngleDeg.toFixed(2)}deg`,
        }}
      >
        <span className="belot-auction-reveal-trail" aria-hidden />
        <span className="belot-auction-reveal-sheen" aria-hidden />
        <p className="belot-auction-reveal-kicker">Контракт объявлен</p>
        <div className="belot-auction-reveal-main">
          <span className="belot-auction-reveal-points">{fx.points || '—'}</span>
          <span className={cn('belot-auction-reveal-orb', trumpView.tone)}>
            <span className="belot-auction-reveal-impact" aria-hidden />
            <span className="belot-auction-reveal-orb-ring belot-auction-reveal-orb-ring-a" aria-hidden />
            <span className="belot-auction-reveal-orb-ring belot-auction-reveal-orb-ring-b" aria-hidden />
            <span className="belot-auction-reveal-orb-core">{trumpView.line1}</span>
          </span>
        </div>
        {multiplier > 1 ? (
          <p
            className={cn(
              'belot-auction-reveal-multiplier',
              multiplier >= 4 && 'belot-auction-reveal-multiplier-high',
            )}
          >
            Множитель ×{multiplier}
          </p>
        ) : null}
      </div>
    </div>
  );
}
